import type { MembershipDetailsPage } from '@mentora/api-public'
import type { ReactNode } from 'react'
import type { CancelMembershipAction } from './membership-actions-card'
import type { RetryRefundAction } from './retry-refund-button'
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mentora/ui-components'
import { useFormatter, useTranslations } from 'next-intl'
import { MembershipActionsCard } from './membership-actions-card'
import { RefundHistory } from './refund-history'
import { BuyerCard } from './transaction-overview'
import { useStatusLabel } from './use-status-label'

// One membership, for the superadmin: the member, the subscription as Stripe
// reports it right now (renewal, latest payment), the admin's cancel/refund,
// and every refund made.

function Row({ label, children }: { label: string, children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-xs text-muted-foreground sm:text-sm">{label}</dt>
      <dd className="text-sm break-all text-foreground sm:text-right">{children}</dd>
    </div>
  )
}

export function MembershipDetailsPanel({ page, cancelMembership, retryRefund }: {
  page: MembershipDetailsPage
  cancelMembership: CancelMembershipAction
  retryRefund: RetryRefundAction
}) {
  const t = useTranslations('transactions.membership')
  const format = useFormatter()
  const statusLabel = useStatusLabel()
  const { record, billing } = page
  const applicationLabel: Record<string, string> = {
    pending: t('applicationStatus.pending'),
    approved: t('applicationStatus.approved'),
    rejected: t('applicationStatus.rejected'),
  }
  const date = (d: Date | string | null) => d ? format.dateTime(new Date(d), { dateStyle: 'medium' }) : t('absent')
  const money = (cents: number, currency: string) => format.number(cents / 100, { style: 'currency', currency: currency.toUpperCase() })

  const live = billing.kind === 'stripe'
  const payment = live ? billing.latestPayment : null
  const ended = live && (billing.status === 'canceled' || billing.status === 'incomplete_expired')

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{record.customerName ?? record.customerEmail ?? record.userId}</CardTitle>
            <Badge variant="secondary">{statusLabel(record.subscriptionStatus)}</Badge>
          </div>
          {record.customerEmail && <CardDescription>{record.customerEmail}</CardDescription>}
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border/60">
            <Row label={t('application')}>{applicationLabel[record.applicationStatus] ?? record.applicationStatus}</Row>
            <Row label={t('subscriptionId')}><span className="font-mono">{record.stripeSubscriptionId ?? t('absent')}</span></Row>
            <Row label={t('since')}>{date(record.createdAt)}</Row>
            {billing.kind === 'stripe' && (
              <>
                <Row label={t('renewal')}>
                  {billing.cancelAtPeriodEnd
                    ? t('endsOn', { date: date(billing.currentPeriodEnd) })
                    : ended ? t('ended') : t('renewsOn', { date: date(billing.currentPeriodEnd) })}
                </Row>
                <Row label={t('latestPayment')}>
                  {payment
                    ? `${money(payment.amountPaidCents, payment.currency)} · ${date(payment.paidAt)}`
                    : t('noPayment')}
                </Row>
                {payment && payment.refundedCents > 0 && (
                  <Row label={t('alreadyRefunded')}>{money(payment.refundedCents, payment.currency)}</Row>
                )}
              </>
            )}
            {billing.kind === 'unavailable' && <Row label={t('renewal')}>{t('stripeUnavailable')}</Row>}
            {billing.kind === 'none' && <Row label={t('renewal')}>{t('noSubscription')}</Row>}
          </dl>
        </CardContent>
      </Card>

      <BuyerCard buyer={record.buyer} />

      {live && !ended && (
        <MembershipActionsCard
          membershipId={record.id}
          refundable={payment ? { cents: Math.max(payment.amountPaidCents - payment.refundedCents, 0), currency: payment.currency } : null}
          endsAtPeriodEnd={billing.cancelAtPeriodEnd}
          cancelMembership={cancelMembership}
        />
      )}

      <RefundHistory refunds={record.refunds} retry={retryRefund} />
    </div>
  )
}
